import { showToast } from '../../../engine/toast';
import {
  adminGetFullPaymentConfig, adminPatchPaymentConfig,
  type AdminDepositMethod, type AdminPayoutMethod, type AdminFullPaymentConfig,
} from '../admin-api';
import { toggleSwitch } from '../admin-ui';
import type { PaneModule } from './types';

function statusBadge(m: { configured: boolean; enabled: boolean; active: boolean }): string {
  if (m.active) return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-green-900/40 text-green-400">Active</span>';
  if (!m.configured) return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-neutral-800 text-text-dim">Not configured</span>';
  if (!m.enabled) return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-neutral-800 text-text-dim">Disabled</span>';
  return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/40 text-yellow-400">Inactive</span>';
}

function centsToEur(cents: number): string { return (cents / 100).toFixed(2); }
function eurToCents(eur: string): number { return Math.round(parseFloat(eur) * 100); }

function billingToggle(method: AdminDepositMethod, busy: boolean): string {
  const isRequired = method.billing === 'required';
  const locked = method.minBilling === 'required';
  return `<button type="button" data-action="toggle-billing-${method.id}"
    class="text-[10px] px-1.5 py-0.5 rounded border transition-colors touch-manipulation
      ${isRequired ? 'bg-gold/20 border-gold/40 text-gold' : 'bg-surface-2 border-border text-text-dim'}"
    ${busy || !method.configured || locked ? 'disabled' : ''}
    title="${locked ? 'Provider requires billing address' : `Click to ${isRequired ? 'make optional' : 'require'}`}"
  >${isRequired ? 'Required' : 'Optional'}</button>`;
}

function depositTable(methods: AdminDepositMethod[], busy: boolean): string {
  if (methods.length === 0) return '<p class="text-xs text-text-dim">No deposit methods registered.</p>';
  return `<div class="overflow-x-auto"><table class="w-full text-xs">
    <thead><tr class="text-left text-text-dim border-b border-border">
      <th class="py-1.5 pr-3">Method</th>
      <th class="py-1.5 pr-3 hidden sm:table-cell">Currencies</th>
      <th class="py-1.5 pr-3">Billing</th>
      <th class="py-1.5 pr-3">Enabled</th>
      <th class="py-1.5">Status</th>
    </tr></thead>
    <tbody>${methods.map((m) => `<tr class="border-b border-border/50">
      <td class="py-2 pr-3 font-medium">${m.label}</td>
      <td class="py-2 pr-3 text-text-dim hidden sm:table-cell">${m.currencies.length > 0 ? m.currencies.join(', ') : '—'}</td>
      <td class="py-2 pr-3">${billingToggle(m, busy)}</td>
      <td class="py-2 pr-3">${toggleSwitch(`toggle-deposit-${m.id}`, '', '', m.enabled, busy || !m.configured)}</td>
      <td class="py-2">${statusBadge(m)}</td>
    </tr>`).join('')}</tbody>
  </table></div>`;
}

function payoutTable(methods: AdminPayoutMethod[], busy: boolean): string {
  if (methods.length === 0) return '<p class="text-xs text-text-dim">No payout methods registered.</p>';
  return `<div class="overflow-x-auto"><table class="w-full text-xs">
    <thead><tr class="text-left text-text-dim border-b border-border">
      <th class="py-1.5 pr-3">Method</th>
      <th class="py-1.5 pr-3 hidden sm:table-cell">Receiver</th>
      <th class="py-1.5 pr-3 hidden sm:table-cell">Currencies</th>
      <th class="py-1.5 pr-3">Enabled</th>
      <th class="py-1.5">Status</th>
    </tr></thead>
    <tbody>${methods.map((m) => `<tr class="border-b border-border/50">
      <td class="py-2 pr-3 font-medium">${m.label}</td>
      <td class="py-2 pr-3 text-text-dim hidden sm:table-cell">${m.receiver === 'iban' ? 'IBAN' : 'Card'}</td>
      <td class="py-2 pr-3 text-text-dim hidden sm:table-cell">${m.currencies.join(', ')}</td>
      <td class="py-2 pr-3">${toggleSwitch(`toggle-payout-${m.id}`, '', '', m.enabled, busy || !m.configured)}</td>
      <td class="py-2">${statusBadge(m)}</td>
    </tr>`).join('')}</tbody>
  </table></div>`;
}

function limitsForm(config: AdminFullPaymentConfig, busy: boolean): string {
  const l = config.limits;
  const inp = (name: string, value: string) =>
    `<input type="number" step="0.01" name="${name}" value="${value}"
       class="w-24 bg-surface border border-border rounded px-2 py-1 text-xs text-right" ${busy ? 'disabled' : ''}>`;
  return `<form data-form="save-limits" class="flex flex-col gap-4">
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <fieldset class="flex flex-col gap-2">
        <legend class="font-heading text-xs text-gold tracking-wide uppercase mb-1">Deposit Limits</legend>
        <label class="flex items-center justify-between gap-2 text-xs"><span>Fee %</span>${inp('depositFeePct', String(l.depositFeePct))}</label>
        <label class="flex items-center justify-between gap-2 text-xs"><span>Min (EUR)</span>${inp('depositMinEur', centsToEur(l.depositMinCents))}</label>
        <label class="flex items-center justify-between gap-2 text-xs"><span>Max (EUR)</span>${inp('depositMaxEur', centsToEur(l.depositMaxCents))}</label>
        <label class="flex items-center justify-between gap-2 text-xs"><span>KYC threshold (EUR)</span>${inp('depositKycThresholdEur', centsToEur(l.depositKycThresholdCents))}</label>
        ${toggleSwitch('toggle-require-email', 'Require verified email', '', l.requireVerifiedEmail, busy)}
      </fieldset>
      <fieldset class="flex flex-col gap-2">
        <legend class="font-heading text-xs text-gold tracking-wide uppercase mb-1">Withdrawal Limits</legend>
        <label class="flex items-center justify-between gap-2 text-xs"><span>Fee %</span>${inp('withdrawalFeePct', String(l.withdrawalFeePct))}</label>
        <label class="flex items-center justify-between gap-2 text-xs"><span>Min (EUR)</span>${inp('withdrawalMinEur', centsToEur(l.withdrawalMinCents))}</label>
        <label class="flex items-center justify-between gap-2 text-xs"><span>Max (EUR)</span>${inp('withdrawalMaxEur', centsToEur(l.withdrawalMaxCents))}</label>
        ${toggleSwitch('toggle-cap-to-deposits', 'Cap to deposited amount', '', l.capToDeposits, busy)}
      </fieldset>
    </div>
    <button type="submit" class="self-start px-4 py-1.5 bg-gold text-bg text-xs font-bold rounded hover:brightness-110 disabled:opacity-50 touch-manipulation" ${busy ? 'disabled' : ''}>Save limits</button>
  </form>`;
}

export const paymentsPane: PaneModule = {
  id: 'payments',
  label: 'Payments',

  html(ctx) {
    const config = ctx.state.payments.config;
    if (!config) return '<div class="text-xs text-text-dim">Loading payment configuration&hellip;</div>';

    return `<section class="flex flex-col gap-5">
      ${!config.adopted ? `<div class="text-xs text-gold bg-gold/10 border border-gold/30 rounded p-2.5">
        Payments not adopted yet. Method toggles are saved but won&rsquo;t take effect until a payment ledger is registered.
      </div>` : ''}
      <div>
        <h2 class="font-heading text-xs text-gold mb-2 tracking-wide uppercase">Deposit Methods</h2>
        ${depositTable(config.deposit, ctx.state.busy)}
      </div>
      <div>
        <h2 class="font-heading text-xs text-gold mb-2 tracking-wide uppercase">Payout Methods</h2>
        ${payoutTable(config.payout, ctx.state.busy)}
      </div>
      <div>
        <h2 class="font-heading text-xs text-gold mb-2 tracking-wide uppercase">Limits &amp; Fees</h2>
        ${limitsForm(config, ctx.state.busy)}
      </div>
    </section>`;
  },

  async load(ctx) {
    try {
      ctx.state.payments.config = await adminGetFullPaymentConfig();
    } catch {
      showToast('Failed to load payment config.', 'error');
    }
  },

  onAction(action, _el, ctx) {
    const depositToggle = action.match(/^toggle-deposit-(.+)$/);
    if (depositToggle) {
      const id = depositToggle[1];
      const m = ctx.state.payments.config?.deposit.find((d) => d.id === id);
      if (!m) return true;
      void ctx.withBusy(async () => {
        ctx.state.payments.config = await adminPatchPaymentConfig({ deposit: { [id]: { enabled: !m.enabled } } });
        showToast(`${m.label} ${!m.enabled ? 'enabled' : 'disabled'}.`, 'success');
      }, 'Could not update deposit method.');
      return true;
    }

    const payoutToggle = action.match(/^toggle-payout-(.+)$/);
    if (payoutToggle) {
      const id = payoutToggle[1];
      const m = ctx.state.payments.config?.payout.find((p) => p.id === id);
      if (!m) return true;
      void ctx.withBusy(async () => {
        ctx.state.payments.config = await adminPatchPaymentConfig({ payout: { [id]: { enabled: !m.enabled } } });
        showToast(`${m.label} ${!m.enabled ? 'enabled' : 'disabled'}.`, 'success');
      }, 'Could not update payout method.');
      return true;
    }

    const billingToggle = action.match(/^toggle-billing-(.+)$/);
    if (billingToggle) {
      const id = billingToggle[1];
      const m = ctx.state.payments.config?.deposit.find((d) => d.id === id);
      if (!m) return true;
      const next = m.billing === 'required' ? 'optional' : 'required';
      void ctx.withBusy(async () => {
        ctx.state.payments.config = await adminPatchPaymentConfig({ deposit: { [id]: { billing: next } } });
        showToast(`${m.label} billing: ${next}.`, 'success');
      }, 'Could not update billing requirement.');
      return true;
    }

    if (action === 'toggle-require-email') {
      const current = ctx.state.payments.config?.limits.requireVerifiedEmail ?? false;
      void ctx.withBusy(async () => {
        ctx.state.payments.config = await adminPatchPaymentConfig({ limits: { requireVerifiedEmail: !current } });
        showToast(`Require verified email ${!current ? 'enabled' : 'disabled'}.`, 'success');
      }, 'Could not update email requirement.');
      return true;
    }

    if (action === 'toggle-cap-to-deposits') {
      const current = ctx.state.payments.config?.limits.capToDeposits ?? false;
      void ctx.withBusy(async () => {
        ctx.state.payments.config = await adminPatchPaymentConfig({ limits: { capToDeposits: !current } });
        showToast(`Cap to deposits ${!current ? 'enabled' : 'disabled'}.`, 'success');
      }, 'Could not update cap setting.');
      return true;
    }

    return false;
  },

  onSubmit(kind, form, ctx) {
    if (kind !== 'save-limits') return false;
    const val = (name: string) => (form.querySelector(`[name="${name}"]`) as HTMLInputElement)?.value ?? '0';
    void ctx.withBusy(async () => {
      ctx.state.payments.config = await adminPatchPaymentConfig({
        limits: {
          depositFeePct: parseFloat(val('depositFeePct')),
          depositMinCents: eurToCents(val('depositMinEur')),
          depositMaxCents: eurToCents(val('depositMaxEur')),
          depositKycThresholdCents: eurToCents(val('depositKycThresholdEur')),
          withdrawalFeePct: parseFloat(val('withdrawalFeePct')),
          withdrawalMinCents: eurToCents(val('withdrawalMinEur')),
          withdrawalMaxCents: eurToCents(val('withdrawalMaxEur')),
        },
      });
      showToast('Limits saved.', 'success');
    }, 'Could not save limits.');
    return true;
  },
};
